import { getApiOrigin } from '@/config/env';
import {
  getSelectedOtherPrevBrandReason,
  readOtherPrevBrandAnswerAnswers,
} from '@/lib/otherPrevBrandAnswerStorage';
import { hasLdMotivationSelected, isLdProductNowComplete, readLdProductNowAnswers } from '@/lib/ldProductNowStorage';
import { readOtherPrevBrandAnswers } from '@/lib/otherPrevBrandStorage';
import { readPrevBrandAnswerAnswers, getSelectedPrevBrandReason } from '@/lib/prevBrandAnswerStorage';
import { readPrevBrandAnswers } from '@/lib/prevBrandStorage';
import { readSetAProfileAnswers } from '@/lib/setAProfileStorage';
import { readSetBLdBrandOpinionAnswers } from '@/lib/setBLdBrandOpinionStorage';
import { readWhyNotLdAnswers } from '@/lib/whyNotLdStorage';
import { OTP_DRAFT_STORAGE_KEY, SURVEY_SCREENING_STORAGE_KEY, type SurveyScreeningAnswers } from '@/stores/appSlice';
import type { SetBLdMotivationKey } from '@shared/constants/setBLdMotivation';
import { isSurveyLanguage, SURVEY_LANGUAGE_STORAGE_KEY } from '@shared/constants/surveyLanguage';
import type { SurveyLanguage } from '@shared/types/surveyLanguage';

function fetchFailureHint(): string {
  const origin = getApiOrigin();
  if (origin.startsWith('http')) {
    return ` Is the API running at ${origin}?`;
  }
  if (import.meta.env.DEV) {
    return ' Is the Vite dev server running (port 5174) with /api proxied to the backend on port 4000?';
  }
  return ' Check VITE_API_URL in .env.';
}

type PrevBrandReasonPayload = {
  type: 'taste' | 'price' | 'productFeatures' | 'brandCredibility' | 'peersSmoking' | 'other';
  elaboration: string;
};

type LdSurveySubmitBodyBase = {
  language: SurveyLanguage;
  smoker: 'yes' | 'no';
  name: string;
  email: string;
  race: string;
  state: string;
  area: string;
  smokedOtherProduct: 'yes' | 'no';
  whyNotLd?: string;
  smokeFrequency?: { firstProductCount: number };
  otherPrevBrand?: {
    brand: string;
    variant: string;
    reason: PrevBrandReasonPayload;
  };
  mobileLocalDigits: string;
};

export type LdSurveySetASubmitBody = LdSurveySubmitBodyBase & {
  questionnaireSet?: 'A';
  prevBrand: string;
  prevVariant: string;
  prevBrandReason: PrevBrandReasonPayload;
};

export type LdSurveySetBSubmitBody = LdSurveySubmitBodyBase & {
  questionnaireSet: 'B';
  setbldvariant: string;
  setbldmotivation: SetBLdMotivationKey;
  setbldmotivationOther?: string;
  setbldbrandopinion?: string;
  setbldfuturelikelihood?: number;
  setbldfuturelikelihoodwhy?: string;
};

export type LdSurveySubmitBody = LdSurveySetASubmitBody | LdSurveySetBSubmitBody;

export type SurveySubmitTngResponse = {
  pin: string;
  expirydate: string | null;
  value: string | null;
  reused: boolean;
};

function readStoredSurveyLanguage(): SurveyLanguage | null {
  try {
    const raw = sessionStorage.getItem(SURVEY_LANGUAGE_STORAGE_KEY);
    return isSurveyLanguage(raw) ? raw : null;
  } catch {
    return null;
  }
}

function readStoredSurveyScreening(): SurveyScreeningAnswers | null {
  try {
    const raw = sessionStorage.getItem(SURVEY_SCREENING_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      typeof (parsed as { ageBand?: unknown }).ageBand !== 'string' ||
      ((parsed as { smoker?: unknown }).smoker !== 'yes' &&
        (parsed as { smoker?: unknown }).smoker !== 'no')
    ) {
      return null;
    }
    return parsed as SurveyScreeningAnswers;
  } catch {
    return null;
  }
}

function readStoredQuestionnaireSet(): 'A' | 'B' | null {
  try {
    const raw = sessionStorage.getItem(OTP_DRAFT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== 'object' || parsed === null) return null;
    const set = (parsed as { questionnaireSet?: unknown }).questionnaireSet;
    return set === 'A' || set === 'B' ? set : null;
  } catch {
    return null;
  }
}

function isProfileComplete(profile: ReturnType<typeof readSetAProfileAnswers>): boolean {
  return Boolean(
    profile?.name?.trim() &&
      profile.email?.trim() &&
      profile.race?.trim() &&
      profile.state?.trim() &&
      profile.area?.trim()
  );
}

function buildOtherProductFields(
  smokedOther: 'yes' | 'no',
  isSetB: boolean
): Pick<LdSurveySubmitBodyBase, 'whyNotLd' | 'otherPrevBrand' | 'smokeFrequency'> | null {
  if (smokedOther === 'no') {
    const whyNotLd = readWhyNotLdAnswers().answer.trim();
    if (!isSetB && !whyNotLd) return null;
    return isSetB ? {} : { whyNotLd };
  }

  const otherBrand = readOtherPrevBrandAnswers();
  const otherAnswer = readOtherPrevBrandAnswerAnswers();
  const otherReason = getSelectedOtherPrevBrandReason(otherAnswer);

  if (
    !otherBrand?.brand?.trim() ||
    !otherBrand.variant?.trim() ||
    !otherReason ||
    otherAnswer.firstProductCount < 0 ||
    otherAnswer.firstProductCount > 10
  ) {
    return null;
  }

  const otherFields = {
    otherPrevBrand: {
      brand: otherBrand.brand.trim(),
      variant: otherBrand.variant.trim(),
      reason: otherReason,
    },
    smokeFrequency: { firstProductCount: otherAnswer.firstProductCount },
  };

  if (isSetB) {
    return otherFields;
  }

  const whyNotLd = readWhyNotLdAnswers().answer.trim();
  if (!whyNotLd) return null;
  return { ...otherFields, whyNotLd };
}

function buildSetBSubmitBody(
  language: SurveyLanguage,
  screening: SurveyScreeningAnswers,
  profile: ReturnType<typeof readSetAProfileAnswers>,
  ldProductNow: ReturnType<typeof readLdProductNowAnswers>,
  digits: string
): LdSurveySetBSubmitBody | null {
  if (!ldProductNow.variant.trim() || !hasLdMotivationSelected(ldProductNow) || !ldProductNow.motivation) {
    return null;
  }

  const smokedOther =
    ldProductNow.smokedOtherProduct || readPrevBrandAnswerAnswers().smokedOtherProduct;
  if (smokedOther !== 'yes' && smokedOther !== 'no') return null;

  const otherFields = buildOtherProductFields(smokedOther, true);
  if (!otherFields) return null;

  const base: LdSurveySetBSubmitBody = {
    questionnaireSet: 'B',
    language,
    smoker: screening.smoker,
    name: profile?.name?.trim() ?? '',
    email: profile?.email?.trim() ?? '',
    race: profile?.race?.trim() ?? '',
    state: profile?.state?.trim() ?? '',
    area: profile?.area?.trim() ?? '',
    setbldvariant: ldProductNow.variant.trim(),
    setbldmotivation: ldProductNow.motivation,
    ...(ldProductNow.motivation === 'other'
      ? { setbldmotivationOther: ldProductNow.motivationOtherSpecify.trim() }
      : {}),
    smokedOtherProduct: smokedOther,
    ...otherFields,
    mobileLocalDigits: digits,
  };

  const opinion = readSetBLdBrandOpinionAnswers();
  if (
    !opinion.brandOpinion.trim() ||
    !opinion.futureLikelihood ||
    !opinion.futureLikelihoodWhy.trim()
  ) {
    return null;
  }
  base.setbldbrandopinion = opinion.brandOpinion.trim();
  base.setbldfuturelikelihood = Number(opinion.futureLikelihood);
  base.setbldfuturelikelihoodwhy = opinion.futureLikelihoodWhy.trim();

  return base;
}

function buildSetASubmitBody(
  language: SurveyLanguage,
  screening: SurveyScreeningAnswers,
  profile: ReturnType<typeof readSetAProfileAnswers>,
  digits: string
): LdSurveySetASubmitBody | null {
  if (!isProfileComplete(profile)) return null;

  const prevBrand = readPrevBrandAnswers();
  const prevBrandAnswer = readPrevBrandAnswerAnswers();
  const selectedReason = getSelectedPrevBrandReason(prevBrandAnswer);
  const smokedOther = prevBrandAnswer.smokedOtherProduct;

  if (
    !prevBrand?.brand?.trim() ||
    !prevBrand.variant?.trim() ||
    !selectedReason ||
    (smokedOther !== 'yes' && smokedOther !== 'no')
  ) {
    return null;
  }

  const otherFields = buildOtherProductFields(smokedOther, false);
  if (!otherFields) return null;

  return {
    language,
    smoker: screening.smoker,
    name: profile.name.trim(),
    email: profile.email.trim(),
    race: profile.race.trim(),
    state: profile.state.trim(),
    area: profile.area.trim(),
    prevBrand: prevBrand.brand.trim(),
    prevVariant: prevBrand.variant.trim(),
    prevBrandReason: selectedReason,
    smokedOtherProduct: smokedOther,
    ...otherFields,
    mobileLocalDigits: digits,
  };
}

/** Builds LD survey POST body from sessionStorage + localStorage drafts. */
export function buildLdSurveySubmitBodyFromStorage(mobileLocalDigits: string): LdSurveySubmitBody | null {
  const language = readStoredSurveyLanguage();
  const screening = readStoredSurveyScreening();
  if (!language || !screening) return null;

  const questionnaireSet = readStoredQuestionnaireSet();
  const isSetB = questionnaireSet === 'B' || isLdProductNowComplete();

  const digits = mobileLocalDigits.replace(/\D/g, '');
  if (!digits) return null;

  const profile = readSetAProfileAnswers();
  const ldProductNow = readLdProductNowAnswers();

  if (isSetB) {
    return buildSetBSubmitBody(language, screening, profile, ldProductNow, digits);
  }

  return buildSetASubmitBody(language, screening, profile, digits);
}

export async function submitCustomerSurveyTng(
  customerUuid: string,
  body: LdSurveySubmitBody
): Promise<SurveySubmitTngResponse> {
  let res: Response;
  try {
    res = await fetch(`${getApiOrigin()}/api/v1/customers/${customerUuid}/survey-submit-tng`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch (e) {
    throw new Error(
      e instanceof Error && e.message === 'Failed to fetch'
        ? `Could not reach the server.${fetchFailureHint()}`
        : e instanceof Error
          ? e.message
          : 'Network error'
    );
  }

  const data = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    error?: string;
    details?: unknown;
    pin?: string;
    expirydate?: string | null;
    value?: string | null;
    reused?: boolean;
  };

  if (!res.ok) {
    const msg = typeof data.error === 'string' ? data.error : `Save failed (${res.status}).`;
    const flat = data.details as { fieldErrors?: Record<string, unknown> } | undefined;
    const fe = flat?.fieldErrors;
    const hint =
      fe != null && Object.keys(fe).length > 0
        ? ` (${Object.entries(fe)
            .map(([k, v]) => `${k}: ${String(v)}`)
            .join('; ')})`
        : '';
    throw new Error(msg + hint);
  }

  if (data.ok !== true || typeof data.pin !== 'string') {
    throw new Error('Unable to save survey or assign PIN');
  }

  return {
    pin: data.pin,
    expirydate: data.expirydate ?? null,
    value: data.value ?? null,
    reused: data.reused === true,
  };
}
